import type {
  PublisherAppImportDraft,
  PublisherAppImportPayload,
  PublisherAppValidationPayload,
} from './model';
import { PublisherAppImportStatus } from './model';

export const publisherAppDomainPattern =
  /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zñáéíóúüA-Z0-9?\\%$#!&_\-{}.*[\]]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

export function splitPublisherAppStoreUrls(value: string) {
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 3);
}

export function createPublisherAppDraft(): PublisherAppImportDraft {
  return {
    rowId: crypto.randomUUID(),
    id: undefined,
    name: undefined,
    appStoreUrl: undefined,
    appStoreId: undefined,
    bundleId: undefined,
    bundleUrl: undefined,
    domain: undefined,
    adsTxtLocation: undefined,
    description: undefined,
    language: undefined,
    platform: null,
    uniqueApp: true,
    invalidApp: false,
    iabCategories: [],
    iabCategoriesIds: [],
    iabCategoryIds: [],
    adswizzGenresIds: [],
  };
}

export function publisherAppImportStatus(app: PublisherAppImportDraft) {
  if (app.uniqueApp === false) return PublisherAppImportStatus.Duplicate;
  if (
    app.invalidApp === true ||
    !app.appStoreId?.trim() ||
    !app.platform?.id ||
    Boolean(app.domain && !publisherAppDomainPattern.test(app.domain))
  ) {
    return PublisherAppImportStatus.Invalid;
  }
  return PublisherAppImportStatus.Valid;
}

export function countPublisherAppImportStatuses(apps: readonly PublisherAppImportDraft[]) {
  return apps.reduce(
    (counts, app) => {
      counts[publisherAppImportStatus(app)] += 1;
      return counts;
    },
    {
      [PublisherAppImportStatus.Valid]: 0,
      [PublisherAppImportStatus.Duplicate]: 0,
      [PublisherAppImportStatus.Invalid]: 0,
    },
  );
}

export function serializePublisherAppsForCreate(
  publisherId: string,
  apps: readonly PublisherAppImportDraft[],
): PublisherAppImportPayload[] {
  return apps.map((app) => ({
    publisherId,
    name: clean(app.name),
    description: clean(app.description),
    bundleId: clean(app.bundleId),
    bundleUrl: clean(app.bundleUrl),
    appStoreId: clean(app.appStoreId),
    appStoreUrl: clean(app.appStoreUrl),
    platform: app.platform?.id ?? '',
    domain: clean(app.domain),
    adsTxtLocation: clean(app.adsTxtLocation),
    uniqueApp: app.uniqueApp !== false,
    invalidApp: app.invalidApp === true,
    iabCategoriesIds: app.iabCategoriesIds,
    adswizzGenresIds: app.adswizzGenresIds,
    language: clean(app.language),
  }));
}

export function serializePublisherAppsForValidation(
  apps: readonly PublisherAppImportDraft[],
): PublisherAppValidationPayload[] {
  return apps.map((app) => ({
    appStoreUrl: clean(app.appStoreUrl),
    name: clean(app.name),
    appStoreId: clean(app.appStoreId),
    platform: app.platform?.id ?? '',
    iabCategoriesIds: app.iabCategoriesIds.length ? app.iabCategoriesIds : null,
    bundleId: clean(app.bundleId),
    domain: clean(app.domain),
  }));
}

function clean(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}
